'use client'

import type { Schema } from 'yup'
import type {
    FailedPartialValidationEventDetail,
    PassedPartialValidationEventDetail,
    RequestValidationEventDetail,
} from '../../../types/EventDetailTypes'
import type { EventDispatchQueue } from '../../UseEventDispatchQueue'
import { TypeOfValidations } from '../../../constants/validation-constants'
import {
    FAILED_PARTIAL_VALIDATION_EVENT,
    PASSED_PARTIAL_VALIDATION_EVENT,
    REQUEST_PARTIAL_VALIDATION_EVENT,
} from '../../../constants/custom-event-names'
import { useEffect } from 'react'
import {
    setupFailedPartialValidationEvent,
    setupPassedPartialValidationEvent,
} from '../../../helpers/setupCustomEvents'
import { getFieldNamesToValidate } from './helpers/getFieldNamesToValidate'
import { validateFieldsAndSetValidity } from './helpers/validateFieldsAndSetValidity'
import { assertIsCustomEvent } from '../helpers/assertIsCustomEvent'

/**
 * @description
 * listens for request-validation events and validates fields that are marked as related on that event.
 * it then:
 * - marks the input fields as valid or invalid
 * - if event.detail.typeOfValidation === WITH_ERRORS then it clears the errors for all related fields
 *   and adds new errors generated for those fields, if any.
 *
 * @memberof useValidateSchema
 *
 * @listens RequestPartialValidation
 *
 * @fires FailedPartialValidation
 * @fires PassedPartialValidation
 *
 * @param schema -- the full yup schema as generated by useSchema
 * @param formElement -- the form element that contains the inputs that need to be validated
 * @param eventQueue -- the eventQueue that is used to manage dispatching of events
 * @param dispatchDelayInMilliseconds -- number of miliseconds that is used for debouncing event
 * dispatching
 * */
export const useTriggerValidation = (
    schema: Schema | null,
    formElement: HTMLFormElement | null,
    eventQueue: EventDispatchQueue<PassedPartialValidationEventDetail | FailedPartialValidationEventDetail>,
    dispatchDelayInMilliseconds: number = 0,
) => {
    useEffect(() => {
        if (!schema || !formElement) {
            return
        }
        const controller = new AbortController()
        document.addEventListener(
            REQUEST_PARTIAL_VALIDATION_EVENT,
            (event) => {
                const customEvent = event as CustomEvent<RequestValidationEventDetail>
                assertIsCustomEvent(customEvent)
                if (formElement.id !== customEvent.detail.formId) {
                    return
                }
                const fields = getFieldNamesToValidate(customEvent)
                const errors = validateFieldsAndSetValidity(fields, schema, formElement)
                const shouldUpdateErrors = customEvent.detail.typeOfValidation === TypeOfValidations.WITH_ERRORS

                if (shouldUpdateErrors && !!errors.length) {
                    const id = FAILED_PARTIAL_VALIDATION_EVENT
                    const event = setupFailedPartialValidationEvent(formElement, errors, fields)
                    eventQueue.queueEvent(id, event, formElement, dispatchDelayInMilliseconds)
                }

                if (shouldUpdateErrors && !errors?.length) {
                    const id = PASSED_PARTIAL_VALIDATION_EVENT
                    const event = setupPassedPartialValidationEvent(formElement, fields)
                    eventQueue.queueEvent(id, event, formElement, dispatchDelayInMilliseconds)
                }
            },
            { signal: controller.signal },
        )
        return () => {
            controller.abort()
        }
    }, [schema, formElement])
}
